import {Component, EventEmitter, inject, Input, NgZone, OnChanges, OnDestroy, Output} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {SmQrScannerOverlayModule} from '../components/sm-qr-scanner-overlay/sm-qr-scanner-overlay.module';
import {CodeScan} from './code-scan';
import {UniversalScannerBackService} from './universal-scanner-back.service';
import {UniversalScannerService} from './universal-scanner.service';

@Component({
  selector: 'sm-universal-scanner',
  imports: [TranslateModule, SmQrScannerOverlayModule],
  templateUrl: './universal-scanner.component.html',
})
export class UniversalScannerComponent implements OnChanges, OnDestroy {
  private readonly scanner = inject(UniversalScannerService);
  private readonly nativeBack = inject(UniversalScannerBackService);
  private readonly zone = inject(NgZone);
  private readonly navigation = inject(Router).events.subscribe(event => {
    if (event instanceof NavigationStart) this.close();
  });
  @Input() isOpen = false;
  @Output() scanned = new EventEmitter<CodeScan>();
  @Output() closeRequested = new EventEmitter<void>();
  webOpen = false;
  dialogOpen = false;
  busy = false;
  errorKey = '';
  qrOnly = false;
  private video?: HTMLVideoElement;
  private revision = 0;
  private destroyed = false;
  private session?: ReturnType<UniversalScannerService['startWeb']>;
  private stopping: Promise<void> = Promise.resolve();
  private nativeAbort?: AbortController;
  private unregisterNativeBack?: () => void;

  ngOnChanges(): void {
    if (this.isOpen) void this.open();
    else this.stop();
  }

  async startWeb(video: HTMLVideoElement): Promise<void> {
    this.video = video;
    if (!this.webOpen || this.session) return;
    const revision = this.revision;
    this.busy = true;
    try {
      this.session = this.scanner.startWeb(video,
        scan => this.zone.run(() => this.accept(scan, revision)),
        () => this.zone.run(() => { if (revision === this.revision) this.qrOnly = true; }));
      await this.session.ready;
    }
    catch { if (revision === this.revision) this.errorKey = 'universalScan.failed'; }
    finally { if (revision === this.revision) this.busy = false; }
  }

  close(): void {
    this.stop();
    this.closeRequested.emit();
  }

  async retry(): Promise<void> {
    if (this.busy || !this.isOpen || !this.errorKey) return;
    const video = this.video;
    const webOpen = this.webOpen;
    this.stop();
    // Keep the video element mounted while the old camera session releases it.
    this.webOpen = webOpen;
    this.dialogOpen = true;
    const opening = this.open();
    const revision = this.revision;
    await opening;
    if (revision === this.revision && video && this.webOpen) await this.startWeb(video);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.navigation.unsubscribe();
    this.stop();
  }

  private async open(): Promise<void> {
    const revision = ++this.revision;
    if (this.scanner.isNative) {
      this.unregisterNativeBack?.();
      this.unregisterNativeBack = this.nativeBack.register(() => this.close());
      this.nativeAbort = new AbortController();
    }
    this.errorKey = '';
    this.qrOnly = false;
    this.busy = true;
    await this.stopping;
    if (this.destroyed || revision !== this.revision) return;
    if (!this.scanner.isNative) {
      this.webOpen = true;
      this.dialogOpen = true;
      return;
    }
    await this.openNative(revision);
  }

  private async openNative(revision: number): Promise<void> {
    try {
      const result = await this.scanner.scanNative(this.nativeAbort?.signal);
      if (revision !== this.revision) return;
      if (result) this.accept(result, revision);
      else this.close();
    } catch {
      if (revision !== this.revision) return;
      this.errorKey = 'universalScan.failed';
      this.dialogOpen = true;
    } finally { if (revision === this.revision) this.busy = false; }
  }

  private accept(scan: CodeScan | null, revision: number): void {
    if (this.destroyed || revision !== this.revision || !this.isOpen) return;
    if (!scan) { this.errorKey = 'barcode.unsupportedCode'; return; }
    this.close();
    this.scanned.emit(scan);
  }

  private stop(): void {
    this.revision++;
    this.nativeAbort?.abort();
    this.nativeAbort = undefined;
    this.unregisterNativeBack?.();
    this.unregisterNativeBack = undefined;
    this.webOpen = false;
    this.dialogOpen = false;
    this.busy = false;
    this.errorKey = '';
    this.qrOnly = false;
    this.video = undefined;
    const session = this.session;
    this.session = undefined;
    if (session) this.stopping = session.stop().catch(() => {});
  }
}
